import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import {
  Package,
  ArrowRight,
  ShoppingBag,
  Truck,
  MapPin,
  Star,
  MessageSquare,
  AlertTriangle,
  RotateCcw,
  CheckCircle,
  Clock,
  Calendar
} from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import axios from "axios";
import { getOrderShipping, MISSION_STATUS, hasTransportDelivery } from "../services/transporteurService";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

console.log("OrdersPage: Using API_URL:", API_URL);

const safeDate = (dateStr) => {
  try {
    if (!dateStr) return new Date();
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? new Date() : d;
  } catch (e) {
    return new Date();
  }
};

// ── Barre de progression produit — utilise transporteur_status comme source de vérité ──

function _ProductProgressBar({ order }) {
  const tStatus = Object.values(order.shipping_info || {})
    .find(v => v?.transporteur_status)?.transporteur_status;
  const oStatus = order.order_status;
  const itemDelivered = (order.items || []).some(i =>
    ["DELIVERED", "COMPLETED", "DELIVERY_CONFIRMED"].includes(i.delivery_status)
  );
  const isDelivered = oStatus === 'DELIVERED' || tStatus === 'delivered' || itemDelivered;
  const isShipped   = !isDelivered && (oStatus === 'SHIPPED'   || tStatus === 'in_transit');
  const isPreparing = !isDelivered && !isShipped && (
    oStatus === 'PROCESSING' || tStatus === 'accepted' ||
    (order.items || []).some(i => i.delivery_status === 'PREPARING')
  );
  const barWidth = isDelivered ? '100%' : isShipped ? '75%' : isPreparing ? '50%' : '25%';

  return (
    <div className="relative pt-6 pb-2">
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className="h-full bg-green-500 transition-all duration-500" style={{ width: barWidth }} />
      </div>
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        <span>Confirmée</span>
        <span>Prép.</span>
        <span>Expédiée</span>
        <span className={isDelivered ? 'text-green-600 font-semibold' : ''}>
          Livrée {isDelivered ? '✓' : ''}
        </span>
      </div>
    </div>
  );
}

// ── Composant suivi transporteur ──────────────────────────────────────────────

const STEPS = ["accepted", "in_transit", "delivered"];

function ShippingTracker({ orderId }) {
  const [missions, setMissions] = useState(null);
  const [loadingShip, setLoadingShip] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("buymoreeasy_token");
    if (!token) { setLoadingShip(false); return; }
    getOrderShipping(orderId, token)
      .then((d) => setMissions(d.missions || []))
      .catch(() => setMissions([]))
      .finally(() => setLoadingShip(false));
  }, [orderId]);

  if (loadingShip) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
        <span className="inline-block w-3 h-3 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
        Vérification livraison…
      </div>
    );
  }

  if (!missions || missions.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      {missions.map((m) => {
        const info    = MISSION_STATUS[m.status] || MISSION_STATUS.accepted;
        const stepIdx = STEPS.indexOf(m.status);

        const colorMap = {
          blue:   "bg-blue-100 text-blue-700 border-blue-200",
          amber:  "bg-amber-100 text-amber-700 border-amber-200",
          violet: "bg-violet-100 text-violet-700 border-violet-200",
          green:  "bg-green-100 text-green-700 border-green-200",
          red:    "bg-red-100 text-red-700 border-red-200",
        };
        const dotMap = {
          blue: "bg-blue-500", amber: "bg-amber-500",
          violet: "bg-violet-500", green: "bg-green-500", red: "bg-red-500",
        };

        return (
          <div key={m.mission_id}
            className={`rounded-lg border px-3 py-2.5 text-xs ${colorMap[info.color] || colorMap.amber}`}>
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <span className="font-semibold flex items-center gap-1.5">
                <Truck className="h-3.5 w-3.5" />
                Transporteur — {m.status === "in_transit" ? "En route" : info.label}
              </span>
              {m.fee != null && (
                <span className="font-medium opacity-75">{m.fee.toLocaleString()} HTG</span>
              )}
            </div>

            {/* Progress bar */}
            {m.status !== "cancelled" && (
              <div className="flex items-center gap-1.5">
                {STEPS.map((step, i) => {
                  const done = stepIdx >= i;
                  return (
                    <div key={step} className="flex items-center gap-1.5 flex-1">
                      <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 border-2 ${
                        done ? `${dotMap[info.color]} border-transparent` : "bg-white border-gray-300"
                      }`} />
                      {i < STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 ${done && stepIdx > i ? dotMap[info.color] : "bg-gray-200"}`} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Code de suivi */}
            {m.tracking_code && (
              <div className="mt-1.5 flex items-center gap-2">
                <a
                  href={`${process.env.REACT_APP_TRANSPORTEUR_TRACKING_URL || "http://localhost:3002"}/track/${m.tracking_code}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-white/60 border border-current hover:bg-white/90 transition"
                >
                  <span>📍</span>
                  {m.tracking_code}
                  <span className="opacity-60 text-[10px]">↗</span>
                </a>
                <span className="text-[10px] opacity-60">Suivre mon colis</span>
              </div>
            )}

            {m.pickup_at && (
              <p className="mt-1 opacity-75">
                Récupéré : {new Date(m.pickup_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
              </p>
            )}
            {m.delivered_at && (
              <p className="mt-0.5 font-semibold">
                Livré : {new Date(m.delivered_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── OrderCard ─────────────────────────────────────────────────────────────────

const OrderCard = ({ order, handleOpenDetails, statusLabels }) => (
  <Card className="overflow-hidden hover:shadow-md transition-shadow">
    <CardContent className="p-6">
      <div
        className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
        onClick={() => handleOpenDetails(order)}
      >
        <div>
          <div className="flex items-center gap-3 mb-1">
            <span className="font-heading font-semibold">
              #{order.order_number}
            </span>
            <Badge variant={statusLabels[order.order_status]?.variant || "secondary"}>
              {statusLabels[order.order_status]?.label || order.order_status}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {format(safeDate(order.created_at), "d MMMM yyyy 'à' HH:mm", {
              locale: fr,
            })}
          </p>
        </div>
        <div className="text-right">
          <p className="font-heading font-bold text-xl text-primary">
            {(order.total_amount || 0).toFixed(2)} HTG
          </p>
          <p className="text-sm text-muted-foreground">
            {order.items?.length || 0} article(s)
          </p>
        </div>
      </div>

      {order.items && order.items.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {order.items.slice(0, 4).map((item) => (
              <div
                key={item.id}
                className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0 relative group"
              >
                <img
                  src={
                    item.product_image ||
                    item.product?.images?.[0] ||
                    "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=400"
                  }
                  alt={item.product_title || ""}
                  className="w-full h-full object-cover"
                />
                {/* Badge for quantity */}
                <span className="absolute bottom-0 right-0 bg-black/60 text-white text-[10px] px-1">x{item.quantity}</span>
              </div>
            ))}
          </div>
          {/* Show booking dates if any */}
          {order.items?.some(i => i.booking_date) && (
            <div className="mt-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
              <strong>Rendez-vous prévus :</strong>
              <ul className="list-disc pl-4 mt-1">
                {order.items.filter(i => i.booking_date).map(item => (
                  <li key={item.id}>
                    {item.product?.title} : {format(safeDate(item.booking_date), "d MMMM yyyy 'à' HH:mm", { locale: fr })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suivi transporteur */}
          {hasTransportDelivery(order) && (
            <ShippingTracker orderId={order.id} />
          )}
        </div>
      )}
    </CardContent>
  </Card>
);

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const moncashPaid = searchParams.get("paid") === "1";
  const paidOrderNumber = searchParams.get("order");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "", product_id: "" });
  const [activeTab, setActiveTab] = useState("purchases");

  // Dispute State
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeStatusOpen, setDisputeStatusOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [disputeDescription, setDisputeDescription] = useState("");
  const [disputeEvidenceUrls, setDisputeEvidenceUrls] = useState([""]);
  const [disputeOrder, setDisputeOrder] = useState(null);
  const [currentDispute, setCurrentDispute] = useState(null);
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [extraEvidenceUrls, setExtraEvidenceUrls] = useState(['']);
  const [addingEvidence, setAddingEvidence] = useState(false);

  // ── Retours ─────────────────────────────────────────────────────────────
  const [returnModal, setReturnModal] = useState(null);  // { itemId }
  const [returnReason, setReturnReason] = useState('');
  const [returnDesc, setReturnDesc] = useState('');
  const [returnLoading, setReturnLoading] = useState(false);
  const [respondLoading, setRespondLoading] = useState(false);

  const RETURN_REASONS = [
    "Article défectueux / endommagé",
    "Produit non conforme à la description",
    "Mauvaise taille / couleur",
    "Article non reçu",
    "Commande en double",
    "Je me suis trompé de commande",
    "Autre",
  ];

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("buymoreeasy_token");
      if (!token) {
        setError("Non authentifié. Veuillez vous connecter.");
        setLoading(false);
        return;
      }
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const ordersRes = await axios.get(`${API_URL}/orders`, config);
      if (typeof ordersRes.data === 'string' && ordersRes.data.trim().startsWith('<!')) {
        throw new Error("L'API a renvoyé du HTML. Vérifiez que le backend tourne sur le port 8002.");
      }
      setOrders(ordersRes.data || []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Impossible de charger les commandes.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      await axios.put(`${API_URL}/orders/${orderId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Statut mis à jour");
      fetchOrders(); // Refresh
    } catch (e) {
      toast.error("Erreur lors de la mise à jour");
    }
  };
  const handleReview = (product_id) => {
    setReviewForm({ rating: 5, comment: "", product_id });
    setReviewOpen(true);
  };

  const submitReview = async () => {
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      await axios.post(`${API_URL}/reviews`, {
        product_id: reviewForm.product_id,
        order_id: selectedOrder.id,
        rating: parseInt(reviewForm.rating),
        comment: reviewForm.comment
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Avis envoyé !");
      setReviewOpen(false);
    } catch (e) {
      console.error("Review Submit Error:", e);
      const errorMsg = e.response?.data?.detail
        ? (typeof e.response.data.detail === 'string' ? e.response.data.detail : JSON.stringify(e.response.data.detail))
        : "Erreur lors de l'envoi de l'avis";
      toast.error(errorMsg);
    }
  };

  const handleCancelOrder = (order) => {
    setOrderToCancel(order);
    setCancelDialogOpen(true);
  };

  const confirmCancelOrder = async () => {
    if (!orderToCancel) return;

    try {
      const token = localStorage.getItem("buymoreeasy_token");
      await axios.put(`${API_URL}/orders/${orderToCancel.id}/status`,
        { status: "CANCELLED" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Commande annulée avec succès");
      if (orderToCancel.payment_method === 'wallet') {
        toast.info("Le montant a été remboursé sur votre Wallet.");
      }
      fetchOrders(); // Refresh list
      setDetailsOpen(false); // Close dialog
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.detail || "Impossible d'annuler la commande.";
      toast.error(msg);
    } finally {
      setCancelDialogOpen(false);
      setOrderToCancel(null);
    }
  };

  const handleCancelItem = async (orderId, itemId) => {
    if (!window.confirm("Voulez-vous vraiment annuler cet article ? Un remboursement sera effectué sur votre Wallet.")) return;
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      const res = await axios.post(`${API_URL}/orders/${orderId}/items/${itemId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Article annulé avec succès");
      setSelectedOrder(res.data); // Mettre à jour la vue de la modale
      fetchOrders(); // Mettre à jour la liste en arrière-plan
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'annulation de l'article");
    }
  };

  const handleReturn = async (order) => {
    // Legacy: use dispute/refund flow
    handleOpenDispute(order);
  };

  const handleInitiateReturn = (itemId) => {
    setReturnReason('');
    setReturnDesc('');
    setReturnModal({ itemId });
  };

  const handleSubmitReturn = async () => {
    if (!returnReason) { toast.error("Veuillez sélectionner une raison."); return; }
    setReturnLoading(true);
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      const res = await axios.post(`${API_URL}/returns/initiate`, {
        order_item_id: returnModal.itemId,
        reason: returnReason,
        description: returnDesc,
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success(`Retour initié — code : ${res.data.return_code}`);
      setReturnModal(null);
      setSelectedOrder(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(i =>
            i.id === returnModal.itemId
              ? { ...i, return_status: "INITIATED", return_code: res.data.return_code }
              : i
          ),
        };
      });
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'initiation du retour");
    } finally {
      setReturnLoading(false);
    }
  };

  const handleRespondPartial = async (returnCode, accept) => {
    setRespondLoading(true);
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      const res = await axios.post(`${API_URL}/returns/${returnCode}/respond`,
        { accept },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(accept
        ? `Offre acceptée — ${res.data.refunded} HTG remboursés sur votre wallet`
        : "Offre refusée — un litige a été ouvert");
      fetchOrders();
      setDetailsOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setRespondLoading(false);
    }
  };

  const handleConfirmReceipt = async (order) => {
    if (!window.confirm("Confirmez-vous la bonne réception de la commande ? Cela libérera les fonds au vendeur.")) return;
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      await axios.post(`${API_URL}/orders/${order.id}/confirm`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Réception confirmée ! Merci.");
      fetchOrders();
      setDetailsOpen(false);
    } catch (e) {
      toast.error("Erreur confirmation: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleOpenDispute = (order) => {
    setDisputeOrder(order);
    setDisputeReason("");
    setDisputeDescription("");
    setDisputeEvidenceUrls([""]);
    setDisputeDialogOpen(true);
  };

  const handleViewDisputeStatus = async (order) => {
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      const res = await axios.get(`${API_URL}/orders/${order.id}/dispute`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentDispute(res.data.dispute);
      setDisputeStatusOpen(true);
    } catch (e) {
      toast.error("Impossible de charger le litige");
    }
  };

  const handleAddEvidence = async () => {
    if (!currentDispute || !selectedOrder) return;
    const files = extraEvidenceUrls.filter(f => f instanceof File);
    if (!files.length) { toast.error("Sélectionnez au moins un fichier."); return; }
    setAddingEvidence(true);
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const res = await axios.post(
        `${API_URL}/orders/${selectedOrder.id}/dispute/upload-evidence`, fd,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
      );
      toast.success(`${res.data.uploaded?.length || 0} preuve(s) ajoutée(s)`);
      setCurrentDispute(prev => ({
        ...prev,
        evidence_images: [...(prev?.evidence_images || []), ...(res.data.uploaded || [])],
      }));
      setExtraEvidenceUrls([]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'upload");
    } finally { setAddingEvidence(false); }
  };

  const submitDispute = async () => {
    if (!disputeOrder || !disputeReason.trim()) return;
    setDisputeLoading(true);
    try {
      const token = localStorage.getItem("buymoreeasy_token");
      const headers = { Authorization: `Bearer ${token}` };

      // 1. Créer le litige (sans preuves d'abord)
      await axios.post(`${API_URL}/orders/${disputeOrder.id}/dispute`, {
        reason: disputeReason,
        description: disputeDescription,
        evidence_images: [],
      }, { headers });

      // 2. Uploader les fichiers si présents
      const files = disputeEvidenceUrls.filter(f => f instanceof File);
      if (files.length > 0) {
        const fd = new FormData();
        files.forEach(f => fd.append("files", f));
        await axios.post(`${API_URL}/orders/${disputeOrder.id}/dispute/upload-evidence`, fd, {
          headers: { ...headers, "Content-Type": "multipart/form-data" }
        });
      }

      toast.success("Litige ouvert avec preuves. Un administrateur va examiner le dossier.");
      fetchOrders();
      setDisputeDialogOpen(false);
      setDetailsOpen(false);
    } catch (e) {
      toast.error("Erreur litige: " + (e.response?.data?.detail || e.message));
    } finally {
      setDisputeLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    // 1. Filter out pure Service orders (they are in the "Services" tab)
    // If an order has ONLY service items (booking_date/booking_start or product_type=SERVICE), hide it from "Purchases"
    const isServiceOrder = order.items?.some(i =>
      i.booking_date ||
      i.booking_start ||
      i.product?.product_type === 'SERVICE' ||
      i.product?.product_type === 'ProductType.SERVICE'
    );

    if (activeTab === 'purchases' && isServiceOrder) return false;

    // Explicitly hide non-service orders from "Services" tab (though TabsContent logic handles display too)
    if (activeTab === 'services' && !isServiceOrder) return false;

    return true;
  });

  // Debugging: Log orders to inspect structure
  useEffect(() => {
    if (orders.length > 0) {
      console.log("Loaded orders:", orders);
      orders.forEach(o => {
        if (o.items?.some(i => i.product?.product_type === 'SERVICE' || i.booking_date)) {
          console.log("Found Service Order:", o);
        }
      });
    }
  }, [orders]);

  const statusLabels = {
    PENDING: { label: "En attente", variant: "secondary" },
    PAID: { label: "Payée", variant: "default" },
    PROCESSING: { label: "En cours", variant: "outline" },
    SHIPPED: { label: "Expédiée", variant: "default" },
    DELIVERED: { label: "Livrée", variant: "default" },
    CANCELLED: { label: "Annulée", variant: "destructive" },
    REFUNDED: { label: "Remboursée", variant: "destructive" },
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
        {/* Bannière succès MonCash */}
        {moncashPaid && (
          <div className="mb-6 flex items-start gap-3 bg-green-50 border border-green-200 rounded-xl px-5 py-4 text-green-800">
            <CheckCircle className="h-5 w-5 mt-0.5 shrink-0 text-green-600" />
            <div>
              <p className="font-semibold">Paiement MonCash confirmé !</p>
              <p className="text-sm text-green-700 mt-0.5">
                {paidOrderNumber
                  ? <>Commande <strong>#{paidOrderNumber}</strong> reçue et en cours de traitement.</>
                  : "Votre commande a bien été reçue et est en cours de traitement."}
              </p>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-2">
            Mes commandes
          </h1>
          <p className="text-muted-foreground">
            Suivez vos commandes et celles de vos boutiques
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-6 flex items-center gap-3" role="alert">
            <AlertTriangle className="h-5 w-5" />
            <span className="block sm:inline">{error}</span>
            <Button variant="outline" size="sm" onClick={fetchOrders} className="ml-auto border-red-200 hover:bg-red-100 text-red-700">
              Réessayer
            </Button>
          </div>
        )}

        <Tabs defaultValue="purchases" className="space-y-6" onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="purchases" data-testid="tab-purchases">
              <ShoppingBag className="mr-2 h-4 w-4" />
              Mes achats
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              <Calendar className="mr-2 h-4 w-4" />
              Services
            </TabsTrigger>
          </TabsList>

          <TabsContent value="purchases">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="border rounded-xl overflow-hidden bg-white shadow-sm animate-pulse">
                    <div className="p-4 border-b flex justify-between items-center">
                      <div className="space-y-2">
                        <div className="h-3 w-28 bg-gray-200 rounded" />
                        <div className="h-3 w-20 bg-gray-100 rounded" />
                      </div>
                      <div className="h-6 w-20 bg-orange-100 rounded-full" />
                    </div>
                    <div className="p-4 flex gap-4">
                      <div className="w-14 h-14 bg-gray-200 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 w-3/4 bg-gray-200 rounded" />
                        <div className="h-3 w-1/3 bg-gray-100 rounded" />
                        <div className="h-3 w-1/2 bg-gray-100 rounded" />
                      </div>
                      <div className="h-5 w-16 bg-gray-200 rounded" />
                    </div>
                    <div className="px-4 pb-4 flex gap-2">
                      <div className="h-8 w-24 bg-gray-100 rounded-lg" />
                      <div className="h-8 w-24 bg-gray-100 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredOrders.length > 0 ? (
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <OrderCard key={order.id} order={order} handleOpenDetails={handleOpenDetails} statusLabels={statusLabels} />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <ShoppingBag className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">
                  Aucune commande
                </h3>
                <p className="text-muted-foreground mb-6">
                  Vous n&apos;avez pas encore passé de commande.
                </p>
                <Link to="/catalogue">
                  <Button>
                    Parcourir le catalogue
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="services">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="h-32 bg-muted animate-pulse rounded-xl"
                  ></div>
                ))}
              </div>
            ) : orders.filter(o => o.items?.some(i => i.booking_date || i.booking_start || i.product?.product_type === 'SERVICE' || i.product?.product_type === 'ProductType.SERVICE')).length > 0 ? (
              <div className="space-y-4">
                {orders.filter(o => o.items?.some(i => i.booking_date || i.booking_start || i.product?.product_type === 'SERVICE' || i.product?.product_type === 'ProductType.SERVICE')).map((order) => (
                  <Card key={order.id} className="overflow-hidden hover:shadow-md transition-shadow border-blue-100">
                    <CardContent className="p-6">
                      <div
                        className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4 cursor-pointer hover:bg-gray-50 p-2 rounded transition-colors"
                        onClick={() => handleOpenDetails(order)}
                      >
                        <div>
                          <div className="flex items-center gap-3 mb-1">
                            <Calendar className="h-5 w-5 text-blue-600" />
                            <span className="font-heading font-semibold">
                              #{order.order_number}
                            </span>
                            <Badge variant={statusLabels[order.order_status]?.variant || "secondary"}>
                              {statusLabels[order.order_status]?.label || order.order_status}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Réservé le {format(safeDate(order.created_at), "d MMMM yyyy 'à' HH:mm", {
                              locale: fr,
                            })}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-heading font-bold text-xl text-blue-600">
                            {(order.total_amount || 0).toFixed(2)} HTG
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {order.items?.filter(i => i.booking_date || i.booking_start || i.product?.product_type === 'SERVICE' || i.product?.product_type === 'ProductType.SERVICE').length || 0} rendez-vous
                          </p>
                        </div>
                      </div>

                      {/* Service Details */}
                      <div className="space-y-3 mt-4">
                        {order.items?.filter(i => i.booking_date || i.booking_start || i.product?.product_type === 'SERVICE' || i.product?.product_type === 'ProductType.SERVICE').map((item) => (
                          <div key={item.id} className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                            <div className="flex items-start gap-4">
                              <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex-shrink-0">
                                <img
                                  src={
                                    item.product?.images?.[0] ||
                                    "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=400"
                                  }
                                  alt=""
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-gray-900 mb-2">{item.product_title || item.product?.title}</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                  <div className="flex items-center gap-2 text-blue-700">
                                    <Calendar className="h-4 w-4" />
                                    <span>{format(safeDate(item.booking_start || item.booking_date), "d MMM yyyy", { locale: fr })}</span>
                                  </div>
                                  <div className="flex items-center gap-2 text-blue-700">
                                    <Clock className="h-4 w-4" />
                                    <span>{format(safeDate(item.booking_start || item.booking_date), "HH:mm", { locale: fr })}</span>
                                  </div>
                                  {item.service_location_address && (
                                    <div className="flex items-center gap-2 text-blue-700">
                                      <MapPin className="h-4 w-4" />
                                      <span className="truncate">{item.service_location_address}</span>
                                    </div>
                                  )}
                                </div>
                                {item.delivery_status !== 'CANCELLED' && (order.order_status === 'PENDING' || order.order_status === 'PAID') && (
                                  <div className="mt-2 pt-2 border-t border-blue-100">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 text-xs"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleCancelItem(order.id, item.id);
                                      }}
                                    >
                                      Annuler ce rendez-vous
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Calendar className="h-12 w-12 text-blue-300 mx-auto mb-4" />
                <h3 className="font-heading font-semibold text-lg mb-2">
                  Aucun rendez-vous
                </h3>
                <p className="text-muted-foreground mb-6">
                  Vous n&apos;avez pas encore réservé de services.
                </p>
                <Link to="/catalogue">
                  <Button>
                    Découvrir nos services
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <Footer />

      {/* Order Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center mr-8">
              <span>Commande #{selectedOrder?.order_number}</span>
              <Badge variant={statusLabels[selectedOrder?.order_status]?.variant}>{statusLabels[selectedOrder?.order_status]?.label}</Badge>
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              {/* Dynamic Progress Bar */}
              {selectedOrder.items?.some(i => i.booking_date || i.product?.product_type === 'SERVICE') ? (
                // Service Progress
                <div className="relative pt-6 pb-2">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-500"
                      style={{ width: selectedOrder.order_status === 'COMPLETED' || selectedOrder.order_status === 'DELIVERED' ? '100%' : selectedOrder.order_status === 'IN_PROGRESS' ? '75%' : selectedOrder.order_status === 'SCHEDULED' ? '50%' : '25%' }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-2">
                    <span>Confirmée</span>
                    <span>Programmé</span>
                    <span>En cours</span>
                    <span>Terminé</span>
                  </div>
                </div>
              ) : (
                <_ProductProgressBar order={selectedOrder} />
              )}

              {/* Info Blocks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedOrder.items?.some(i => i.booking_date || i.product?.product_type === 'SERVICE') ? (
                  // Service Info
                  <>
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 text-blue-800"><Calendar className="h-4 w-4" /> Rendez-vous</h4>
                      {selectedOrder.items.filter(i => i.booking_date).map((item, idx) => (
                        <div key={idx} className="mb-2 last:mb-0 text-sm">
                          <p className="font-medium text-blue-900">{item.product_title}</p>
                          <p className="text-blue-700">
                            {format(new Date(item.booking_date), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><MapPin className="h-4 w-4" /> Lieu du service</h4>
                      <p className="text-sm">
                        {selectedOrder.items.find(i => i.service_location_address)?.service_location_address || "En boutique"}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {selectedOrder.shipping_address?.city}
                      </p>
                    </div>
                  </>
                ) : (
                  // Product Info
                  <>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><Truck className="h-4 w-4" /> Livraison</h4>
                      <p className="text-sm">Type: {selectedOrder.shipping_info?.provider || "Standard"}</p>
                      {selectedOrder.travel_fee > 0 && (
                        <p className="text-sm">Frais : {selectedOrder.travel_fee} HTG</p>
                      )}
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 flex items-center gap-2"><MapPin className="h-4 w-4" /> Adresse</h4>
                      {selectedOrder.shipping_address ? (
                        <>
                          <p className="text-sm font-medium">{selectedOrder.shipping_address.name || ""}</p>
                          <p className="text-sm">{selectedOrder.shipping_address.address}</p>
                          <p className="text-sm text-muted-foreground">
                            {[selectedOrder.shipping_address.city, selectedOrder.shipping_address.region, selectedOrder.shipping_address.country_code?.toUpperCase()].filter(Boolean).join(", ")}
                          </p>
                        </>
                      ) : (
                        <p className="text-sm text-muted-foreground">Adresse non disponible</p>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Order Status History Timeline */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                <h4 className="font-semibold mb-3 flex items-center gap-2 text-primary">
                  <Clock className="h-4 w-4" /> Historique de la commande
                </h4>
                <div className="space-y-4 pl-2 border-l-2 border-gray-200 ml-2">
                  {(selectedOrder.status_history && selectedOrder.status_history.length > 0) ? (
                    (() => {
                      // Dédupliquer par statut (garder le premier de chaque statut)
                      const uniqueHistory = selectedOrder.status_history.filter(
                        (step, i, arr) => arr.findIndex(s => s.status === step.status) === i
                      );
                      return uniqueHistory.map((step, i) => (
                        <div key={i} className="relative pl-6">
                          <div className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 bg-white box-content ${i === uniqueHistory.length - 1 ? 'border-primary bg-primary/10' : 'border-gray-300'}`} />
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline">
                            <span className={`font-medium text-sm ${i === uniqueHistory.length - 1 ? 'text-primary' : 'text-gray-700'}`}>
                              {statusLabels[step.status]?.label || step.status}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {format(safeDate(step.timestamp), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                            </span>
                          </div>
                          {step.note && <p className="text-xs text-gray-500 mt-0.5">{step.note}</p>}
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="relative pl-6">
                      <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 border-gray-300 bg-white box-content" />
                      <div className="flex flex-col">
                        <span className="font-medium text-sm text-gray-700">Création de la commande</span>
                        <span className="text-xs text-muted-foreground">
                          {format(safeDate(selectedOrder.created_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Items groupés par vendeur */}
              <div className="space-y-3">
                {(() => {
                  // Grouper les items par store_id
                  const groups = {};
                  (selectedOrder.items || []).forEach(item => {
                    const sid = item.store_id || "unknown";
                    if (!groups[sid]) groups[sid] = { storeName: item.store_name || item.product?.store?.name || "Boutique", items: [] };
                    groups[sid].items.push(item);
                  });

                  const DELIVERY_MODE_LABELS = {
                    STORE_DELIVERY: { label: "Boutique", icon: "🏪" },
                    TRANSPORT: { label: "Transporteur", icon: "🚚" },
                    PICKUP: { label: "Retrait", icon: "📦" },
                  };

                  const ITEM_STATUS_CONFIG = {
                    PENDING: { label: "En attente", color: "bg-gray-100 text-gray-700" },
                    PREPARING: { label: "Préparation", color: "bg-blue-100 text-blue-700" },
                    SHIPPED: { label: "Expédié", color: "bg-orange-100 text-orange-700" },
                    IN_TRANSIT: { label: "En route", color: "bg-yellow-100 text-yellow-700" },
                    DELIVERED: { label: "Livré ✓", color: "bg-green-100 text-green-700" },
                    DELIVERY_CONFIRMED: { label: "Confirmé ✓", color: "bg-green-100 text-green-700" },
                    COMPLETED: { label: "Terminé ✓", color: "bg-green-100 text-green-700" },
                    SCHEDULED: { label: "Programmé", color: "bg-purple-100 text-purple-700" },
                    IN_PROGRESS: { label: "En cours", color: "bg-indigo-100 text-indigo-700" },
                    CANCELLED: { label: "Annulé", color: "bg-red-100 text-red-700" },
                  };

                  return Object.entries(groups).map(([storeId, group]) => {
                    const storeShipping = selectedOrder.shipping_info?.[storeId] || {};
                    const trackingCode  = storeShipping.tracking_code;
                    const transportStatus = storeShipping.transporteur_status;
                    // Dédupliquer par statut (garder le premier de chaque statut)
                    const rawSteps = group.items[0]?.delivery_tracking_steps || [];
                    const trackingSteps = rawSteps.filter((s, i, arr) =>
                      arr.findIndex(x => x.status === s.status) === i
                    );
                    const TRACKING_STATUS = {
                      accepted:   { label: 'Livreur assigné',     icon: '🚴', color: 'text-purple-700' },
                      in_transit: { label: 'Colis en route',       icon: '🚚', color: 'text-orange-600' },
                      delivered:  { label: 'Livré au client',      icon: '✅', color: 'text-green-600'  },
                      cancelled:  { label: 'Livraison annulée',    icon: '❌', color: 'text-red-500'    },
                    };

                    return (
                    <div key={storeId} className="border rounded-lg overflow-hidden">
                      {/* En-tête Vendeur */}
                      <div className="bg-gray-50 border-b px-4 py-2 flex items-center gap-2 flex-wrap">
                        <span className="text-sm">🏪</span>
                        <span className="font-semibold text-sm text-gray-800">{group.storeName}</span>
                        <span className="text-xs text-gray-400">{group.items.length} article(s)</span>
                        <div className="ml-auto flex items-center gap-2">
                          {/* Bouton contact vendeur */}
                          {group.items[0]?.store_owner_id && (
                            <button
                              onClick={() => {
                                sessionStorage.setItem("msg_pending_contact", JSON.stringify({
                                  id:    group.items[0].store_owner_id,
                                  name:  group.storeName,
                                  order: selectedOrder.order_number,
                                }));
                                navigate("/messages");
                              }}
                              className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 hover:bg-orange-50 px-2 py-1 rounded-lg border border-orange-200 transition-colors"
                            >
                              💬 Contacter
                            </button>
                          )}
                          {/* Tracking code badge */}
                          {trackingCode && (
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-orange-50 text-orange-700 border border-orange-200">
                              📍 {trackingCode}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bloc suivi transporteur */}
                      {(trackingCode || transportStatus) && (
                        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b px-4 py-3">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs font-bold text-indigo-700 uppercase tracking-wide flex items-center gap-1.5">
                              🚚 Suivi de livraison
                            </p>
                            {trackingCode && (
                              <span className="font-mono text-xs font-bold text-gray-500 bg-white px-2 py-0.5 rounded border">
                                {trackingCode}
                              </span>
                            )}
                          </div>

                          {/* Steps de tracking */}
                          {trackingSteps.length > 0 ? (
                            <div className="space-y-1.5">
                              {trackingSteps.map((step, i) => {
                                const cfg = TRACKING_STATUS[step.status] || { label: step.label || step.status, icon: '📦', color: 'text-gray-600' };
                                const isLast = i === trackingSteps.length - 1;
                                return (
                                  <div key={i} className={`flex items-start gap-2 ${isLast ? 'opacity-100' : 'opacity-60'}`}>
                                    <span className="text-base flex-shrink-0 mt-0.5">{cfg.icon}</span>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-xs font-semibold ${isLast ? cfg.color : 'text-gray-600'}`}>
                                        {step.label || cfg.label}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {step.timestamp ? new Date(step.timestamp).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : ''}
                                      </p>
                                    </div>
                                    {isLast && (
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                                        step.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                        step.status === 'in_transit' ? 'bg-orange-100 text-orange-700' :
                                        step.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                                        'bg-purple-100 text-purple-700'
                                      }`}>
                                        {TRACKING_STATUS[step.status]?.label || step.status}
                                      </span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : transportStatus ? (
                            <div className="flex items-center gap-2">
                              <span className="text-base">{TRACKING_STATUS[transportStatus]?.icon || '📦'}</span>
                              <p className={`text-xs font-semibold ${TRACKING_STATUS[transportStatus]?.color || 'text-gray-700'}`}>
                                {TRACKING_STATUS[transportStatus]?.label || transportStatus}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      )}

                      {/* Items du vendeur */}
                      {group.items.map(item => {
                        const modeInfo = DELIVERY_MODE_LABELS[item.delivery_mode] || null;
                        const statusInfo = ITEM_STATUS_CONFIG[item.delivery_status] || { label: item.delivery_status || "En attente", color: "bg-gray-100 text-gray-700" };
                        // isDone = vrai si livraison confirmée par n'importe quelle source
                        const storeTransporteurStatus = selectedOrder.shipping_info?.[storeId]?.transporteur_status;
                        const isDone =
                          ["DELIVERED", "COMPLETED", "DELIVERY_CONFIRMED"].includes(item.delivery_status) ||
                          ["DELIVERED", "COMPLETED"].includes(selectedOrder.order_status) ||
                          storeTransporteurStatus === 'delivered';
                        return (
                          <div key={item.id} className="p-4 border-b last:border-0">
                            <div className="flex items-start gap-3">
                              <img
                                src={item.product_image || item.product?.images?.[0] || "https://images.pexels.com/photos/5632399/pexels-photo-5632399.jpeg?auto=compress&cs=tinysrgb&w=400"}
                                className="w-14 h-14 object-cover rounded bg-gray-100 flex-shrink-0"
                                alt={item.product_title || ""}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{item.product_title}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{item.quantity} × {item.unit_price} HTG</p>

                                {/* Badges : mode de livraison + statut */}
                                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                  {modeInfo && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">
                                      {modeInfo.icon} {modeInfo.label}
                                    </span>
                                  )}
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                                    {statusInfo.label}
                                  </span>
                                  {item.tracking_number && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-50 text-gray-600 border border-gray-200">
                                      📋 {item.tracking_number}
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Action Avis ou Annulation ou Retour */}
                              <div className="flex gap-2">
                                {/* Avis si terminé / livré */}
                                {isDone && !item.return_status && (
                                  <Button variant="outline" size="sm" className="flex-shrink-0" onClick={() => handleReview(item.product_id)}>
                                    <Star className="h-3.5 w-3.5 mr-1" /> Avis
                                  </Button>
                                )}

                                {/* Retour seulement si Livré (pas encore de retour initié) ET produit retournable */}
                                {/* Retourner : visible si livré ET retournable
                                    is_returnable peut être null (= True par défaut pour physiques) */}
                                {isDone && !item.return_status &&
                                  (item.is_returnable !== false) && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex-shrink-0 text-orange-600 border-orange-200 hover:bg-orange-50"
                                    onClick={() => handleInitiateReturn(item.id)}
                                  >
                                    <RotateCcw className="h-3.5 w-3.5 mr-1" /> Retourner
                                  </Button>
                                )}

                                {/* Statut du retour */}
                                {item.return_status === "INITIATED" && (
                                  <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">
                                    ⏳ En attente vendeur ({item.return_code})
                                  </Badge>
                                )}
                                {item.return_status === "PARTIAL_OFFER" && (() => {
                                  const step = (item.delivery_tracking_steps || []).find(s => s.status === "PARTIAL_OFFER");
                                  return (
                                    <div className="flex flex-col gap-1.5 w-full mt-1">
                                      <p className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                                        💬 Offre partielle : {step?.amount ?? "?"} HTG
                                      </p>
                                      <div className="flex gap-2">
                                        <Button size="sm" variant="outline"
                                          className="border-green-500 text-green-700 hover:bg-green-50 text-xs h-7"
                                          disabled={respondLoading}
                                          onClick={() => handleRespondPartial(item.return_code, true)}
                                        >✓ Accepter</Button>
                                        <Button size="sm" variant="outline"
                                          className="border-red-400 text-red-600 hover:bg-red-50 text-xs h-7"
                                          disabled={respondLoading}
                                          onClick={() => handleRespondPartial(item.return_code, false)}
                                        >✕ Refuser & Litige</Button>
                                      </div>
                                    </div>
                                  );
                                })()}
                                {item.return_status === "COMPLETED" && (
                                  <Badge className="bg-green-100 text-green-700 border-green-300">
                                    ✓ Retour remboursé
                                  </Badge>
                                )}
                                {item.return_status === "DISPUTED" && (
                                  <Badge variant="outline" className="border-red-400 text-red-600 bg-red-50">
                                    ⚠️ En litige ({item.return_code})
                                  </Badge>
                                )}

                                {!isDone && item.delivery_status !== 'CANCELLED' && item.delivery_status !== 'REFUNDED' &&
                                  (selectedOrder.order_status === 'PENDING' || selectedOrder.order_status === 'PAID' || selectedOrder.order_status === 'PROCESSING') && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                                      onClick={() => handleCancelItem(selectedOrder.id, item.id)}
                                    >
                                      Annuler
                                    </Button>
                                  )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    );
                  });
                })()}
              </div>


              {/* Footer Actions */}
              <DialogFooter className="flex justify-between sm:justify-between items-center gap-4">
                {(selectedOrder.order_status === 'PENDING' || selectedOrder.order_status === 'PROCESSING') && (
                  <Button
                    variant="destructive"
                    onClick={() => handleCancelOrder(selectedOrder)}
                  >
                    Annuler la commande
                  </Button>
                )}
                {/* Bouton de retour global retiré au profit du retour par article */}
                <div className="flex gap-2">
                  {/* Confirmer / Signaler : visible si livré (order_status OU transporteur_status) */}
                  {(() => {
                    const tStatus = Object.values(selectedOrder.shipping_info || {})
                      .find(v => v?.transporteur_status)?.transporteur_status;
                    const isDelivered = selectedOrder.order_status === 'DELIVERED' || tStatus === 'delivered';
                    if (!isDelivered) return null;
                    return (
                      <>
                        <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleConfirmReceipt(selectedOrder)}>
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Confirmer Réception
                        </Button>
                        <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleOpenDispute(selectedOrder)}>
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          Signaler un problème
                        </Button>
                      </>
                    );
                  })()}
                  {selectedOrder.order_status === 'DISPUTE' && (
                    <Button variant="outline" className="text-orange-600 border-orange-300" onClick={() => handleViewDisputeStatus(selectedOrder)}>
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Voir mon litige
                    </Button>
                  )}
                  {selectedOrder.order_status === 'DISPUTE_RESOLVED' && (
                    <Button variant="outline" className="text-purple-600 border-purple-300" onClick={() => handleViewDisputeStatus(selectedOrder)}>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Voir la décision
                    </Button>
                  )}
                </div>
                <div className="text-xl font-bold">Total: {(selectedOrder.total_amount || 0).toFixed(2)} HTG</div>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancellation Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous sûr de vouloir annuler ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Pour modifier la date, veuillez annuler puis effectuer une nouvelle réservation.
              {orderToCancel?.payment_method === 'wallet' && (
                <div className="mt-2 text-green-600 font-medium">
                  Le montant de {(orderToCancel?.total_amount || 0).toFixed(2)} HTG sera immédiatement remboursé sur votre Wallet.
                </div>
              )}
              {orderToCancel?.items?.some(i => i.booking_date) && (
                <div className="mt-2 text-amber-600">
                  ⚠️ Pour les services, l'annulation doit se faire au moins 24h avant le rendez-vous.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Retour</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancelOrder} className="bg-red-600 hover:bg-red-700">
              Confirmer l'annulation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Review Dialog */}
      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Donner votre avis</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  className={`h-8 w-8 cursor-pointer ${star <= reviewForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
                  onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                />
              ))}
            </div>
            <Textarea
              placeholder="Partagez votre expérience..."
              value={reviewForm.comment}
              onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
            />
            <Button onClick={submitReview} className="w-full">Envoyer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog (Acheteur) */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" /> Ouvrir un litige
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              ⚠️ Les fonds seront bloqués jusqu'à la résolution par un administrateur. Décrivez le problème avec précision.
            </div>

            <div className="space-y-1.5">
              <Label className="font-semibold">Motif du litige <span className="text-red-500">*</span></Label>
              <select
                className="w-full border rounded-md px-3 py-2 text-sm"
                value={disputeReason}
                onChange={e => setDisputeReason(e.target.value)}
              >
                <option value="">-- Sélectionner un motif --</option>
                <option value="Produit non reçu">Produit non reçu</option>
                <option value="Produit endommagé">Produit endommagé / cassé</option>
                <option value="Produit non conforme">Produit ne correspond pas à la description</option>
                <option value="Article manquant">Article manquant dans la commande</option>
                <option value="Mauvaise qualité">Mauvaise qualité</option>
                <option value="Vendeur non réactif">Vendeur non réactif</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="font-semibold">Description détaillée</Label>
              <Textarea
                value={disputeDescription}
                onChange={e => setDisputeDescription(e.target.value)}
                placeholder="Expliquez en détail ce qui s'est passé, quand, et ce que vous attendez comme résolution..."
                rows={4}
              />
            </div>

            <div className="space-y-2">
              <Label className="font-semibold">Preuves (photos / documents)</Label>
              <p className="text-xs text-gray-500">JPG, PNG, WebP ou PDF — max 5 fichiers, 5 MB chacun</p>
              <input
                type="file"
                accept="image/*,.pdf"
                multiple
                className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
                onChange={e => setDisputeEvidenceUrls(Array.from(e.target.files))}
              />
              {disputeEvidenceUrls.length > 0 && Array.isArray(disputeEvidenceUrls) && typeof disputeEvidenceUrls[0] !== 'string' && (
                <ul className="text-xs text-gray-500 space-y-0.5">
                  {disputeEvidenceUrls.map((f, i) => (
                    <li key={i}>📎 {f.name} ({(f.size / 1024).toFixed(0)} KB)</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeDialogOpen(false)}>Annuler</Button>
            <Button
              variant="destructive"
              onClick={submitDispute}
              disabled={!disputeReason.trim() || disputeLoading}
            >
              {disputeLoading ? "Envoi..." : "Ouvrir le litige"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Status Dialog (Acheteur — suivi litige) */}
      <Dialog open={disputeStatusOpen} onOpenChange={setDisputeStatusOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" /> Suivi du litige
            </DialogTitle>
          </DialogHeader>
          {currentDispute ? (
            <div className="space-y-4 py-2">
              {/* Status Badge */}
              {(() => {
                const statusMap = {
                  OPEN: { label: 'Ouvert — en attente du vendeur', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
                  SELLER_RESPONDED: { label: 'Vendeur a répondu — en attente d\'arbitrage', color: 'bg-blue-100 text-blue-800 border-blue-300' },
                  INVESTIGATION: { label: 'En cours d\'investigation', color: 'bg-purple-100 text-purple-800 border-purple-300' },
                  RESOLVED: { label: 'Résolu ✅', color: 'bg-green-100 text-green-800 border-green-300' },
                  REJECTED: { label: 'Rejeté ❌', color: 'bg-red-100 text-red-800 border-red-300' },
                };
                const s = statusMap[currentDispute.status] || { label: currentDispute.status, color: '' };
                return <div className={`text-center py-2 px-4 rounded-lg border font-semibold text-sm ${s.color}`}>{s.label}</div>;
              })()}

              {/* Buyer's claim */}
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Votre réclamation</p>
                <p className="font-semibold text-sm">{currentDispute.reason}</p>
                {currentDispute.description && <p className="text-sm text-gray-600">{currentDispute.description}</p>}
                {currentDispute.evidence_images?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {currentDispute.evidence_images.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noreferrer" title={`Preuve ${i+1}`}>
                        {/\.(jpg|jpeg|png|webp)/i.test(url)
                          ? <img src={url} alt="" className="w-16 h-16 object-cover rounded border" />
                          : <span className="text-xs text-blue-600 underline">📎 Preuve {i+1}</span>}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              {/* Deadline vendeur */}
              {currentDispute.status === 'OPEN' && currentDispute.seller_deadline && (() => {
                const deadline = new Date(currentDispute.seller_deadline);
                const now = new Date();
                const diffDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
                const isExpired = diffDays <= 0;
                return (
                  <div className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium
                    ${isExpired ? 'bg-red-50 text-red-700 border border-red-200'
                               : diffDays <= 1 ? 'bg-amber-50 text-amber-700 border border-amber-200'
                               : 'bg-gray-50 text-gray-600 border border-gray-200'}`}>
                    <span>⏱️</span>
                    {isExpired
                      ? 'Délai expiré — remboursement automatique en cours'
                      : `Délai réponse vendeur : ${diffDays} jour${diffDays > 1 ? 's' : ''}`}
                  </div>
                );
              })()}

              {/* Montant en jeu */}
              {currentDispute.total_amount > 0 && (
                <div className="flex justify-between text-sm bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-gray-500">Montant du litige</span>
                  <span className="font-bold text-gray-800">{currentDispute.total_amount?.toFixed(2)} HTG</span>
                </div>
              )}

              {/* Seller response */}
              {currentDispute.seller_response ? (
                <div className="bg-blue-50 rounded-lg p-3 space-y-1 border border-blue-100">
                  <p className="text-xs font-bold text-blue-500 uppercase tracking-wide">Réponse du vendeur</p>
                  <p className="text-sm text-blue-900">{currentDispute.seller_response}</p>
                  {currentDispute.seller_evidence_images?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {currentDispute.seller_evidence_images.map((url, i) => (
                        <a key={i} href={url} target="_blank" rel="noreferrer" title={`Preuve vendeur ${i+1}`}>
                          {/\.(jpg|jpeg|png|webp)/i.test(url)
                            ? <img src={url} alt="" className="w-16 h-16 object-cover rounded border" />
                            : <span className="text-xs text-blue-600 underline">📎 Preuve {i+1}</span>}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ) : currentDispute.status === 'OPEN' && (
                <p className="text-sm text-gray-500 italic text-center">En attente de la réponse du vendeur...</p>
              )}

              {/* Ajouter des preuves supplémentaires via upload fichier */}
              {['OPEN', 'SELLER_RESPONDED', 'INVESTIGATION'].includes(currentDispute.status) && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
                  <p className="text-xs font-bold text-orange-700 uppercase tracking-wide">
                    📎 Ajouter des preuves supplémentaires
                  </p>
                  <p className="text-xs text-gray-500">JPG, PNG, WebP, PDF — max 5 fichiers, 5 MB chacun</p>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    multiple
                    className="block w-full text-xs text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-orange-100 file:text-orange-700"
                    onChange={e => setExtraEvidenceUrls(Array.from(e.target.files))}
                  />
                  <Button size="sm" disabled={addingEvidence || !extraEvidenceUrls.length}
                    className="bg-orange-500 hover:bg-orange-600 text-white h-7 text-xs"
                    onClick={handleAddEvidence}>
                    {addingEvidence ? 'Envoi...' : 'Envoyer les preuves'}
                  </Button>
                </div>
              )}

              {/* Admin verdict (only if RESOLVED) */}
              {currentDispute.status === 'RESOLVED' && (
                <div className="bg-purple-50 rounded-lg p-4 space-y-2 border border-purple-200">
                  <p className="text-xs font-bold text-purple-600 uppercase tracking-wide">⚖️ Décision de l'administrateur</p>
                  {(() => {
                    const vmap = {
                      REFUND_CLIENT: `Remboursement intégral : ${(currentDispute.partial_amount_buyer || 0).toFixed ? (currentDispute.partial_amount_buyer || currentDispute.total_amount || '?') : '?'} HTG`,
                      RELEASE_SELLER: 'Décision en faveur du vendeur — aucun remboursement',
                      PARTIAL: `Remboursement partiel : ${currentDispute.partial_amount_buyer || 0} HTG`,
                    };
                    return <p className="font-bold text-purple-900 text-sm">{vmap[currentDispute.admin_verdict] || currentDispute.admin_verdict}</p>;
                  })()}
                  {currentDispute.resolution_notes && (
                    <p className="text-sm text-purple-800 italic">« {currentDispute.resolution_notes} »</p>
                  )}
                  <p className="text-xs text-gray-500">
                    Résolu le {currentDispute.resolved_at ? new Date(currentDispute.resolved_at).toLocaleDateString('fr-FR') : 'N/A'}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-4 text-center">Chargement...</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeStatusOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    {/* ── Modal initiation retour ─────────────────────────────────── */}
    <Dialog open={!!returnModal} onOpenChange={(o) => !o && setReturnModal(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-orange-500" />
            Demande de retour
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold">Raison du retour *</Label>
            <select
              value={returnReason}
              onChange={e => setReturnReason(e.target.value)}
              className="mt-1.5 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
              <option value="">Sélectionner une raison...</option>
              {RETURN_REASONS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div>
            <Label className="text-sm font-semibold">Description (optionnel)</Label>
            <Textarea
              className="mt-1.5 text-sm"
              rows={3}
              placeholder="Décrivez le problème en détail..."
              value={returnDesc}
              onChange={e => setReturnDesc(e.target.value)}
            />
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <p className="text-xs text-amber-700">
              ⚠️ Une fois la demande envoyée, le vendeur examinera votre retour et vous répondra sous 48h.
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setReturnModal(null)} disabled={returnLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmitReturn}
            disabled={returnLoading || !returnReason}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            {returnLoading ? "Envoi..." : "Confirmer le retour"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    </div >
  );
}